/**
 * Reporting contracts — DESIGN.md §10 (R15).
 *
 * "Widget definitions are config objects (`{metric, groupBy, filters, viz}`)
 * rendered by a small chart kit … so new widgets are additions to a registry,
 * not new pages."
 *
 * One request shape and one response shape serve every widget, which is what
 * makes that true. The response carries both a series (for charts) and a table
 * (for the ones §10.2 describes as tables), plus the filters a data point
 * drills down into — "every widget count click-throughs to a pre-filtered card
 * list".
 *
 * The request carries exactly the filter bar §10.2 specifies — date range,
 * projects, assignees, group — and no more. Arbitrary ViewConfig filters live
 * on the *other* side of a drill-down, in the card list the point links to,
 * where the one query engine already knows how to honour them.
 */
import { z } from 'zod';
import { filterSchema } from './view-config.js';
import { dateSchema, uuidSchema } from './common.js';
/** The metric registry — §10.2's four dashboards. */
export const REPORT_METRICS = [
    // Delivery
    'throughput',
    'cycle_time',
    'time_in_status',
    'cumulative_flow',
    'created_vs_completed',
    // Quality & support
    'test_failure_rate',
    'bounce_distribution',
    'docs_compliance',
    'reopened_cards',
    // Time
    'time_logged',
    'effort_by',
    'estimate_vs_actual',
    'effort_vs_elapsed',
    // Team
    'project_health',
    'workload',
    'per_person_delivery',
    'due_date_health',
];
export const REPORT_GROUP_BY = [
    'week',
    'day',
    'month',
    'project',
    'assignee',
    'status',
    'statusCategory',
    'tag',
];
/** §10.2 — "Filter bar on every dashboard: date range (default 30 days), project(s), assignee(s), group." */
export const reportRangeSchema = z
    .object({
    from: dateSchema,
    to: dateSchema,
})
    .strict();
export const reportQuerySchema = z
    .object({
    metric: z.enum(REPORT_METRICS),
    groupBy: z.enum(REPORT_GROUP_BY).optional(),
    range: reportRangeSchema.optional(),
    projectIds: z.array(uuidSchema).max(50).default([]),
    assigneeIds: z.array(uuidSchema).max(100).default([]),
    groupIds: z.array(uuidSchema).max(50).default([]),
})
    .strict();
export const reportPointSchema = z.object({
    /** The x value: an ISO date for time series, a name otherwise. */
    x: z.string(),
    y: z.number(),
    /** A second measure, where the widget shows two (p50 and p85, say). */
    y2: z.number().nullish(),
    y3: z.number().nullish(),
    /** How many cards or entries this point summarises. */
    count: z.number().int().nonnegative().nullish(),
    /**
     * The filters that reproduce this point as a card list — §10.2's
     * "every widget count click-throughs to a pre-filtered card list".
     */
    drilldown: z.array(filterSchema).nullish(),
});
export const reportSeriesSchema = z.object({
    key: z.string(),
    label: z.string(),
    points: z.array(reportPointSchema),
});
export const reportResultSchema = z.object({
    metric: z.enum(REPORT_METRICS),
    /** What the numbers mean, in one line, shown under the widget title. */
    description: z.string(),
    /** Chart-shaped. Empty for table-only widgets. */
    series: z.array(reportSeriesSchema),
    /** Table-shaped, for the widgets §10.2 describes as tables. */
    table: z
        .object({
        columns: z.array(z.object({ key: z.string(), label: z.string(), numeric: z.boolean() })),
        rows: z.array(z.record(z.union([z.string(), z.number(), z.null()]))),
    })
        .nullable(),
    /** The headline figure, when there is one. */
    total: z.number().nullable(),
    /** The unit `total` and `y` are in — 'cards', 'days', 'hours', 'percent'. */
    unit: z.string(),
    range: reportRangeSchema,
    /**
     * §10.3 — set when the result was narrowed to the requesting user because
     * per-person metrics are "visible to admins and each user about themselves".
     */
    scopedToSelf: z.boolean(),
});
//# sourceMappingURL=reports.js.map